import { type SubmitHandler, useForm } from 'react-hook-form';

import * as api from '@gitbook/api-client';
import { SHARE_LINK_NAME_MAX_LENGTH } from '@gitbook/api-client';
import { FormButton } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Alert,
    Button,
    confirm,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    DialogTrigger,
    FieldError,
    Form,
    Heading,
    InlineInputEdit,
    LoadingIndicator,
    Pagination,
    Submit,
    Switch,
    TextField,
    TextInput,
    Tooltip,
    toast,
    useDialogAction,
} from '@gitbook/spine-react';
import { useLoadingState } from '@gitbook/util-react-hooks';

import { CopyableURL } from '../../../../Components/URL/CopyableURL';
import {
    apiClient,
    apiResource,
    useSiteContext,
    useSuspensePaginatedAPIQuery,
    useUpgradeSite,
} from '../../../../state';

const DEFAULT_SHARE_LINK_NAME = 'Untitled';

export function VisibilityStateShareLinks() {
    const { site } = useSiteContext();
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesShareLinks);

    return (site.published && site.visibility === api.SiteVisibility.ShareLink) || upgrade ? (
        <ShareLinkList />
    ) : (
        <Alert
            kind="info"
            details={
                site.published
                    ? 'Update the audience below to start creating share-links.'
                    : 'You must publish your site before being able to create share links.'
            }
        />
    );
}

function ShareLinkList() {
    const { organizationId, site } = useSiteContext();
    const {
        data: shareLinks,
        next,
        previous,
    } = useSuspensePaginatedAPIQuery(
        apiResource('listSiteShareLinks', { organizationId, siteId: site.id })
    );

    return (
        <div className="flex max-h-80 grow flex-col gap-4 overflow-y-auto px-2">
            {shareLinks?.items.map((shareLink) => {
                return <ShareLinkItem key={shareLink.id} shareLink={shareLink} />;
            })}
            <Pagination next={next} previous={previous} />
            {shareLinks.items?.length ? (
                <CreateShareLinkButton />
            ) : (
                <FormButton
                    label="Create share link"
                    labelDescription="Generate a secret public share link for your visitors."
                >
                    <CreateShareLinkButton />
                </FormButton>
            )}
        </div>
    );
}

function CreateShareLinkButton() {
    const { site } = useSiteContext();
    const upgrade = useUpgradeSite(site, api.SiteFeatureKey.SitesShareLinks);

    return (
        <DialogRoot>
            <DialogTrigger>
                <Button
                    disabled={!!upgrade}
                    leadingIcon={Icon.Plus}
                    label="Create share link"
                    kind="secondary"
                    className="w-fit self-center"
                />
            </DialogTrigger>
            <CreateShareLinkDialog />
        </DialogRoot>
    );
}

function CreateShareLinkDialog() {
    return (
        <Dialog>
            <CreateShareLinkForm />
        </Dialog>
    );
}

type CreateShareLinkInputs = {
    name: string;
};

function CreateShareLinkForm() {
    const { site, organizationId } = useSiteContext();
    const [isPending, startDialogAction] = useDialogAction();
    const form = useForm<CreateShareLinkInputs>({ defaultValues: { name: '' } });
    const onSubmit: SubmitHandler<CreateShareLinkInputs> = async (data) => {
        await startDialogAction(async ({ close }) => {
            await toast.catch(async () => {
                await apiClient.siteShareLinks.createSiteShareLink(organizationId, site.id, {
                    name: data.name || DEFAULT_SHARE_LINK_NAME,
                });
                await close();
            }, 'Error while creating share link');
        });
    };

    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogHeader>
                <Heading>Create share link</Heading>
                <Description>
                    Generate a revokable public share link to give access to your site.
                </Description>
            </DialogHeader>
            <DialogBody>
                <TextField name="name">
                    <TextInput placeholder="Name your share link" autoFocus disabled={isPending} />
                    <FieldError />
                </TextField>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Create" />
            </DialogFooter>
        </Form>
    );
}

function ShareLinkItem(props: { shareLink: api.ShareLink }) {
    const { organizationId, site } = useSiteContext();
    const { shareLink } = props;
    const [loading, wrapLoading] = useLoadingState();

    const toggleActiveShareLink = async (props: {
        shareLinkId: api.ShareLink['id'];
        active: boolean;
    }) => {
        const { shareLinkId, active } = props;
        await wrapLoading(async () => {
            await toast.catch(async () => {
                await apiClient.siteShareLinks.updateSiteShareLinkById(
                    organizationId,
                    site.id,
                    shareLinkId,
                    { active }
                );
            }, 'Error while toggling share link');
        });
    };

    const renameShareLink = async (props: { shareLinkId: api.ShareLink['id']; name: string }) => {
        const { shareLinkId, name } = props;
        await wrapLoading(async () => {
            await toast.catch(async () => {
                await apiClient.siteShareLinks.updateSiteShareLinkById(
                    organizationId,
                    site.id,
                    shareLinkId,
                    { name }
                );
            }, 'Error while renaming share link');
        });
    };

    return (
        <div
            key={shareLink.id}
            className="group flex flex-col items-start gap-px rounded-md border border-base bg-muted p-3"
        >
            <div className="flex w-full items-center justify-between gap-4">
                <InlineInputEdit
                    className="-mx-1 px-1 text-ui-action"
                    disabled={loading}
                    onFocus={(event) => {
                        if (shareLink.name === DEFAULT_SHARE_LINK_NAME || !shareLink.name) {
                            event.target.select();
                        }
                    }}
                    onSave={(value, { reset }) => {
                        renameShareLink({
                            shareLinkId: shareLink.id,
                            name: value,
                        }).catch((error) => {
                            reset();
                            throw error;
                        });
                    }}
                    placeholder={DEFAULT_SHARE_LINK_NAME}
                    value={shareLink.name || DEFAULT_SHARE_LINK_NAME}
                    maxLength={SHARE_LINK_NAME_MAX_LENGTH}
                />
                {loading && <LoadingIndicator size={16} />}
            </div>
            <div className="flex max-w-full items-center gap-3">
                {shareLink.urls.published && (
                    <CopyableURL
                        url={shareLink.urls.published}
                        className={loading ? 'opacity-disabled' : undefined}
                    />
                )}
                <Tooltip
                    label={
                        shareLink.active
                            ? 'This share-link is currently active and can be used to access the content.'
                            : 'This share-link is currently inactive and cannot be used to access the content.'
                    }
                >
                    <div className="shrink-0">
                        <Switch
                            value={shareLink.active}
                            onValueChange={(active) =>
                                toggleActiveShareLink({
                                    shareLinkId: shareLink.id,
                                    active,
                                })
                            }
                            disabled={loading}
                            loading={loading}
                        />
                    </div>
                </Tooltip>
                <Button
                    leadingIcon={Icon.Trashcan}
                    kind="secondary"
                    iconOnly
                    label="Revoke"
                    disabled={loading}
                    onPress={() => {
                        void confirm({
                            title: 'Revoke link',
                            description:
                                'Once the share link is revoked, no one can access the content using it. This action cannot be undone.',
                            kind: 'danger',
                            confirmLabel: 'Revoke',
                            onConfirm: async (_event, { preventClose }) => {
                                await toast.catch(
                                    async () => {
                                        await apiClient.siteShareLinks.deleteSiteShareLinkById(
                                            organizationId,
                                            site.id,
                                            shareLink.id
                                        );
                                    },
                                    'Error while revoking share link',
                                    { onReject: preventClose }
                                );
                            },
                        });
                    }}
                />
            </div>
        </div>
    );
}
